/**
 * Reports Store - Phase 5 Implementation
 * Zustand store for reports state management
 * Handles data fetching, caching, and UI state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  ReportsState, 
  ReportsActions,
  ReportType,
  ReportData,
  ReportMetadata,
  ReportTabType,
  DateRange,
  ReportParams,
  ExportConfig,
  ReportPreferences
} from '../types/reports';
import { reportsService } from '../services/reports';

interface ReportsStore extends ReportsState, ReportsActions {}

// Default date range - last 30 days
const getDefaultDateRange = (): DateRange => ({
  start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  end: new Date(),
  preset: 'month'
});

// Default preferences
const getDefaultPreferences = (): ReportPreferences => ({
  defaultDateRange: getDefaultDateRange(),
  preferredChartType: {
    'sales-dashboard': 'line',
    'cash-flow-statement': 'area',
    'profit-loss': 'bar',
    'balance-sheet': 'composed',
    'inventory-status': 'pie',
    'customer-outstanding': 'bar',
    'vendor-payments': 'line'
  },
  defaultExportFormat: 'csv',
  autoRefresh: false,
  refreshInterval: 300000, // 5 minutes
  tablePageSize: 10,
  showAnimations: true
});

const useReportsStore = create<ReportsStore>()(
  persist(
    (set, get) => ({
      // Initial State
      reports: {} as Record<ReportType, ReportData>,
      reportMetadata: {} as Record<ReportType, ReportMetadata>,
      activeReport: null,
      globalDateRange: getDefaultDateRange(),
      isDialogOpen: false,
      activeTab: 'chart',
      isLoading: false,
      isExporting: false,
      error: null,
      exportProgress: 0,
      lastRefresh: {} as Record<ReportType, Date>,
      cacheExpiry: 300000, // 5 minutes
      preferences: getDefaultPreferences(),

      // Data Actions
      fetchReport: async (type: ReportType, params?: ReportParams) => {
        const state = get();
        
        try {
          set({ isLoading: true, error: null });

          // Use global date range if not provided in params
          const requestParams: ReportParams = {
            dateRange: state.globalDateRange,
            ...params
          };

          const response = await reportsService.fetchReport(type, requestParams);
          
          if (response.success && response.data) {
            set(state => ({
              reports: {
                ...state.reports,
                [type]: response.data
              },
              lastRefresh: {
                ...state.lastRefresh,
                [type]: new Date()
              },
              isLoading: false
            }));
          } else {
            set({ 
              error: response.error || 'Failed to fetch report',
              isLoading: false 
            });
          }
        } catch (error) {
          console.error('Error fetching report:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Unknown error occurred',
            isLoading: false 
          });
        }
      },

      fetchReportMetadata: async () => {
        try {
          const response = await reportsService.fetchReportMetadata();
          
          if (response.success && response.data) {
            const metadataMap = response.data.reduce((acc, metadata) => {
              acc[metadata.id] = metadata;
              return acc;
            }, {} as Record<ReportType, ReportMetadata>);

            set({ reportMetadata: metadataMap });
          }
        } catch (error) {
          console.error('Error fetching report metadata:', error);
        }
      },

      refreshReport: async (type: ReportType) => {
        const state = get();
        await state.fetchReport(type);
      },

      refreshAllReports: async () => {
        const state = get();
        const reportTypes = Object.keys(state.reports) as ReportType[];
        
        await Promise.all(
          reportTypes.map(type => state.refreshReport(type))
        );
      },

      fetchAllReports: async () => {
        const state = get();
        // Get all report types from metadata or use default list
        const reportTypes: ReportType[] = Object.keys(state.reportMetadata).length > 0 
          ? Object.keys(state.reportMetadata) as ReportType[]
          : ['sales-dashboard', 'cash-flow-statement', 'profit-loss', 'balance-sheet', 
             'inventory-status', 'customer-outstanding', 'vendor-payments'] as ReportType[];
        
        await Promise.all(
          reportTypes.map(type => state.fetchReport(type))
        );
      },

      // UI Actions
      setActiveReport: (type: ReportType) => {
        set({ activeReport: type });
      },

      openReportDialog: (type: ReportType) => {
        const state = get();
        
        set({ 
          activeReport: type, 
          isDialogOpen: true,
          activeTab: 'chart'
        });

        // Fetch report data if not already loaded or stale
        if (!state.reports[type] || state.isReportStale(type)) {
          state.fetchReport(type);
        }
      },

      closeReportDialog: () => {
        set({ 
          isDialogOpen: false,
          activeReport: null 
        });
      },

      setActiveTab: (tab: ReportTabType) => {
        set({ activeTab: tab });
      },

      setGlobalDateRange: (range: DateRange) => {
        set({ globalDateRange: range });
      },

      // Export Actions
      exportReport: async (type: ReportType, config: ExportConfig) => {
        try {
          set({ isExporting: true, exportProgress: 0 });

          // Simulate progress updates
          const progressInterval = setInterval(() => {
            set(state => ({ 
              exportProgress: Math.min(state.exportProgress + 10, 90) 
            }));
          }, 200);

          const response = await reportsService.exportReport(type, config);
          
          clearInterval(progressInterval);
          set({ exportProgress: 100 });

          if (response.success && response.downloadUrl) {
            // Trigger download
            const link = document.createElement('a');
            link.href = response.downloadUrl;
            link.download = response.filename || `${type}-export.${config.format}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            throw new Error(response.error || 'Export failed');
          }

          setTimeout(() => {
            set({ isExporting: false, exportProgress: 0 });
          }, 1000);

        } catch (error) {
          console.error('Error exporting report:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Export failed',
            isExporting: false,
            exportProgress: 0
          });
        }
      },

      exportMultipleReports: async (types: ReportType[], config: ExportConfig) => {
        try {
          set({ isExporting: true, exportProgress: 0 });
          
          const totalReports = types.length;
          let completed = 0;
          
          for (const type of types) {
            await get().exportReport(type, config);
            completed++;
            set({ exportProgress: Math.round((completed / totalReports) * 100) });
          }
          
          set({ isExporting: false, exportProgress: 0 });
        } catch (error) {
          console.error('Error exporting multiple reports:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Export failed',
            isExporting: false,
            exportProgress: 0
          });
        }
      },

      downloadExport: async (jobId: string) => {
        try {
          const response = await reportsService.downloadExport(jobId);
          
          if (response.success && response.downloadUrl) {
            const link = document.createElement('a');
            link.href = response.downloadUrl;
            link.download = response.filename || 'export';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        } catch (error) {
          console.error('Error downloading export:', error);
        }
      },

      // Preferences Actions
      updatePreferences: (preferences: Partial<ReportPreferences>) => {
        set(state => ({
          preferences: {
            ...state.preferences,
            ...preferences
          }
        }));
      },

      resetPreferences: () => {
        set({ preferences: getDefaultPreferences() });
      },

      // Cache Actions
      clearCache: (type?: ReportType) => {
        if (type) {
          set(state => {
            const { [type]: removed, ...remainingReports } = state.reports;
            const { [type]: removedRefresh, ...remainingRefresh } = state.lastRefresh;
            return {
              reports: remainingReports as Record<ReportType, ReportData>,
              lastRefresh: remainingRefresh as Record<ReportType, Date>
            };
          });
        } else {
          set({ 
            reports: {} as Record<ReportType, ReportData>,
            lastRefresh: {} as Record<ReportType, Date>
          });
        }
      },

      invalidateCache: (type: ReportType) => {
        set(state => {
          const { [type]: removed, ...remainingRefresh } = state.lastRefresh;
          return {
            lastRefresh: remainingRefresh as Record<ReportType, Date>
          };
        });
      },

      // Computed State Getters
      getReportStatus: (type: ReportType) => {
        const state = get();
        const report = state.reports[type];
        
        if (!report) return 'empty';
        return report.status;
      },

      getReportData: (type: ReportType) => {
        const state = get();
        return state.reports[type] || null;
      },

      getAllLoadedReports: () => {
        const state = get();
        return Object.values(state.reports).filter(report => 
          report.status === 'loaded'
        );
      },

      getCachedReports: () => {
        const state = get();
        return Object.keys(state.reports) as ReportType[];
      },

      isReportStale: (type: ReportType) => {
        const state = get();
        const lastRefresh = state.lastRefresh[type];
        
        if (!lastRefresh) return true;
        
        const now = new Date();
        const ageMs = now.getTime() - lastRefresh.getTime();
        return ageMs > state.cacheExpiry;
      }
    }),
    {
      name: 'reports-store',
      partialize: (state) => ({
        // Only persist preferences and date range, not metadata (contains React components)
        preferences: state.preferences,
        globalDateRange: state.globalDateRange
      }),
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          
          const { state } = JSON.parse(str);
          
          // Convert date strings back to Date objects
          if (state.globalDateRange) {
            state.globalDateRange = {
              ...state.globalDateRange,
              start: new Date(state.globalDateRange.start),
              end: new Date(state.globalDateRange.end)
            };
          }
          
          if (state.preferences?.defaultDateRange) {
            state.preferences.defaultDateRange = {
              ...state.preferences.defaultDateRange,
              start: new Date(state.preferences.defaultDateRange.start),
              end: new Date(state.preferences.defaultDateRange.end)
            };
          }
          
          return { state };
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        }
      }
    }
  )
);

export { useReportsStore };